/** Product limits (docs/00-decisions.md). Shared by web + worker. */
export const LIMITS = {
  /** 50 MB per uploaded file */
  maxPhotoBytes: 50 * 1024 * 1024,
  maxPhotosPerAlbum: 1000,
  /** 10 GB per user (originals + fonts) */
  userStorageBytes: 10 * 1024 * 1024 * 1024,
  /** A backup carries every original, so it is bounded by the album, not by the upload limit. */
  maxBackupBytes: 12 * 1024 * 1024 * 1024,
  /** guest albums with no export are purged after this many idle days */
  guestInactivityDays: 30,
  /** originals purged this many days after the last export */
  retentionDays: 90,
  retentionExtensionDays: 90,
  maxFontBytes: 10 * 1024 * 1024,
  maxFontsPerUser: 50,
  /** contributor link uploads per hour (per token) */
  contributorUploadsPerHour: 300,
  contributeLinkDays: 30,
  /** presigned GET ttl for photo urls handed to the editor */
  photoUrlTtlSeconds: 12 * 3600,
  uploadUrlTtlSeconds: 3600,
  exportUrlTtlSeconds: 3600,
  /** presigned urls used by Chromium during a render */
  renderUrlTtlSeconds: 6 * 3600,
  maxPresignPerRequest: 100,
  maxImportItems: 200,
  thumbPx: 320,
  previewPx: 1600,
  flipbookPx: 1600,
  /** variance of Laplacian on 512px grey below this = blurry */
  blurThreshold: 60,
  duplicateHammingMax: 6,
} as const;

export const ALLOWED_PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'] as const;
export type AllowedPhotoMime = (typeof ALLOWED_PHOTO_MIMES)[number];

export const MIME_EXT: Record<AllowedPhotoMime, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
  'image/heif': 'heif',
  'image/webp': 'webp',
};

export const FONT_FORMATS = ['ttf', 'otf', 'woff', 'woff2'] as const;
export type FontFormat = (typeof FONT_FORMATS)[number];
export const FONT_MIME: Record<FontFormat, string> = {
  ttf: 'font/ttf',
  otf: 'font/otf',
  woff: 'font/woff',
  woff2: 'font/woff2',
};

export function extFromMime(mime: string): string | null {
  return (MIME_EXT as Record<string, string>)[mime] ?? null;
}
export function isAllowedPhotoMime(mime: string): mime is AllowedPhotoMime {
  return (ALLOWED_PHOTO_MIMES as readonly string[]).includes(mime);
}
